import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import styled from 'styled-components';

import { ColumnProductLightFragment, GetMicrostoreColumnQuery, useGetColumnProductsQuery } from 'data/__generated__';

import { SectionContainer } from 'components/DetailsView/Section/SectionContainer';
import { DataTable } from 'components/DataTable';
import { Loader, LoaderModeType } from 'components/Loader';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { getColumnStocksColumns } from 'pages/Stocks/constants/columnStocksColumns';

// The table is client-sorted/filtered, so we need the full product list (API defaults to 30 otherwise).
const PRODUCTS_LIMIT = 1000;

export const ProductStocksSection = ({
    microstoreColumn,
}: {
    microstoreColumn: GetMicrostoreColumnQuery['microstoreColumn'];
}) => {
    const columnId = microstoreColumn._id;

    const {
        loading,
        data: productsData,
        error,
    } = useGetColumnProductsQuery({
        variables: { columnId, offset: 0, limit: PRODUCTS_LIMIT },
        skip: !columnId,
    });

    const sortedProducts = useMemo(
        () => (productsData ? [...productsData.columnProducts.columnProducts].sort((a, b) => b.stock - a.stock) : []),
        [productsData],
    );

    const columns = useMemo(() => getColumnStocksColumns(), []);

    return (
        <SectionContainer title="Stocks par produit" isInitiallyOpen={false}>
            <Container>
                <LinkContainer>
                    <Link to={`/column/${columnId}/stocks`}>
                        <TotemPrimaryButton $isSecondaryStyle>Voir la page stocks complète</TotemPrimaryButton>
                    </Link>
                </LinkContainer>
                {loading ? (
                    <Loader mode={LoaderModeType.Spin} />
                ) : error || !productsData ? (
                    <ErrorMessage>Erreur lors de la récupération des stocks</ErrorMessage>
                ) : (
                    <>
                        {productsData.columnProducts.hasMore ? (
                            <TruncationNotice>
                                Liste tronquée aux {PRODUCTS_LIMIT} premiers produits — voir la page stocks complète
                                pour tout afficher.
                            </TruncationNotice>
                        ) : null}
                        <TableContainer>
                            <DataTable<ColumnProductLightFragment>
                                columns={columns}
                                data={sortedProducts}
                                filterMode="client"
                                disableUrlSync
                                getRowId={(row) => row.product._id}
                                getRowLink={(row) => `/column/${columnId}/stocks/${row.product._id}`}
                                searchPlaceholder="Nom du produit"
                                searchFilterKey="searchValue"
                                autoFocusSearch={false}
                            />
                        </TableContainer>
                    </>
                )}
            </Container>
        </SectionContainer>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const LinkContainer = styled.div`
    display: flex;
    justify-content: flex-end;
`;

const TableContainer = styled.div`
    height: 500px;
    overflow: hidden;
`;

const ErrorMessage = styled.div`
    color: ${({ theme }) => theme.errorColor};
    padding: 10px;
`;

const TruncationNotice = styled.div`
    color: ${({ theme }) => theme.infoTextColor};
    font-size: 13px;
`;
